import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type FileAuthResponseData, useFileAuthMutation } from '@core/app/api/filePreviewApi'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './FileImagePreview.scss'

const baseClass = 'FileImagePreview'

const isSafeImageUrl = (url: string): boolean => {
    try {
        const s3Hostnames = [
            's3.amazonaws.com',
            's3.eu-north-1.amazonaws.com',
            's3.eu-west-1.amazonaws.com',
            's3.us-east-1.amazonaws.com',
        ]
        const parsed = new URL(url)

        const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:'
        const isHostnameAllowed = s3Hostnames.some(
            (hostname) => parsed.hostname === hostname || parsed.hostname.endsWith(`.${hostname}`)
        )
        return isHttp && isHostnameAllowed
    } catch {
        return false
    }
}

const getValidatedImageUrl = (rawUrl: unknown): string | undefined => {
    if (typeof rawUrl !== 'string' || rawUrl.length === 0) {
        return
    }

    try {
        const url = new URL(rawUrl)

        if (url.protocol !== 'https:') {
            return
        }

        if (!isSafeImageUrl(url.toString())) {
            return
        }

        return url.toString()
    } catch {
        return
    }
}

const FileImagePreview = () => {
    const { getTitle } = useApplyV2NavTranslations()
    const { previewFile, isOpen, closePreview, getFileName, fileExtension, folder } = useFilePreview()
    const containerRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const [zoom, setZoom] = useState(100)
    const [imageBlob, setImageBlob] = useState<Blob | undefined>(undefined)
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined)
    const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined)
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
    const [fileAuthRequest, { isLoading: isBinaryLoading }] = useFileAuthMutation()
    // const [testBinaryRequest, { isLoading: isBinaryLoading }] = usePostBinaryMutation()
    const scrollTimeout = useRef<NodeJS.Timeout>()
    const [imagesLoaded, setImagesLoaded] = useState(false)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isFitToScreen, setIsFitToScreen] = useState(false)

    const [wsConnected, setWsConnected] = useState(false)
    const [wsError, setWsError] = useState<string | null>(null)
    const [receivedData, setReceivedData] = useState<any>(null)
    const wsRef = useRef<WebSocket | null>(null)
    const [prevOverflow, setPrevOverflow] = useState<string | null>(document.body.style.overflow)

    const handlePrint = () => {
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        document.body.appendChild(iframe)

        const printDocument = iframe.contentDocument || iframe.contentWindow?.document
        if (!printDocument) return

        const imgForPrint = document.createElement('img')
        imgForPrint.src = downloadUrl
        imgForPrint.style.width = '100%'

        // 3. Коли картинка в iframe готова — друкуємо
        imgForPrint.onload = () => {
            iframe.contentWindow?.focus()
            iframe.contentWindow?.print()

            setTimeout(() => {
                document.body.removeChild(iframe)
            }, 1000)
        }

        printDocument.body.appendChild(imgForPrint)
    }

    useEffect(() => {
        const handlePrintKeysPressed = (event: KeyboardEvent) => {
            if (event.key === 'p' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault()
                if (isLoading) return

                handlePrint()
            }
        }

        document.addEventListener('keydown', handlePrintKeysPressed)

        return () => {
            document.removeEventListener('keydown', handlePrintKeysPressed)
        }
    }, [handlePrint, isLoading])

    const connectWebSocket = useCallback(
        (response: FileAuthResponseData) => {
            if (!response.authUrl) {
                setWsError('no URL')
                return
            }
            try {
                if (wsRef.current) {
                    wsRef.current.close()
                }

                setWsConnected(false)
                setWsError(null)

                const ws = new WebSocket(response.authUrl)
                wsRef.current = ws

                ws.onopen = () => {
                    console.log('WebSocket connected')
                    setWsConnected(true)
                    setWsError(null)

                    ws.send(
                        JSON.stringify({
                            // type: 'connection_established',
                            // fileId: previewFile?.CandidateFileID,
                            action: 'v2',
                            data: {
                                extension: response.extension,
                                fileId: response.fileId,
                                folder: response.folder,
                                subscriptionId: response.subscriptionId,
                            },
                        })
                    )
                }

                ws.onmessage = async (event) => {
                    try {
                        const data = JSON.parse(event.data)
                        // console.log('WebSocket message received:', data)
                        setReceivedData(data)

                        const validatedUrl = getValidatedImageUrl(data.url)
                        if (validatedUrl) {
                            setImageUrl(validatedUrl)
                            const img = new Image()
                            img.onload = () => {
                                const width = img.naturalWidth
                                const height = img.naturalHeight
                                setImageDimensions({ width, height })
                            }
                            img.src = validatedUrl
                        }
                        if (data.webSocketStatus === 'done') {
                            ws.close()
                            setWsConnected(false)
                            setWsError(null)
                            wsRef.current = null
                        }
                    } catch (e) {
                        console.log('Received binary data:', event.data)
                    }
                }

                ws.onerror = (error) => {
                    console.error('WebSocket error:', error)
                    setWsError(' WebSocket error')
                    setWsConnected(false)
                }

                ws.onclose = (event) => {
                    console.log('WebSocket disconnected:', event.code, event.reason)
                    setWsConnected(false)

                    if (event.code !== 1000) {
                        // setTimeout(() => {
                        //     if (previewFile && 'CandidateFileID' in previewFile && previewFile?.CandidateFileID) {
                        //         console.log('Attempting to reconnect...')
                        //         connectWebSocket(response)
                        //     }
                        // }, 5000)
                    }
                }
            } catch (error) {
                console.error('WebSocket connection error:', error)
                setWsError('WebSocket connection error')
            }
        },
        [previewFile?.CandidateFileID]
    )

    useEffect(() => {
        // console.log('FileImagePreview useEffect triggered:', { isOpen, previewFile })
        console.log('PREV FILE', previewFile)
        if (!previewFile || !('Ext' in previewFile)) return
        const isIdOrUid = 'CandidateFileID' in previewFile || 'fileUid' in previewFile
        console.log(isIdOrUid)
        if (
            !isOpen ||
            !previewFile ||
            !isIdOrUid ||
            !('Ext' in previewFile)
            // || previewFile.Ext !== 'pdf'
        ) {
            return
        }
        // if (!previewFile?.Ext || !isOpen) return

        const loadImage = async () => {
            if (!previewFile?.CandidateFileID && !previewFile?.fileUid) {
                return
            }

            try {
                let tempApplyFileData = {
                    folder: folder ?? 'candidate_course',
                    fileId: previewFile.CandidateFileID,
                }
                if ('fileUid' in previewFile) {
                    tempApplyFileData = {
                        fileUid: previewFile.fileUid,
                        type: previewFile.type,
                        folder: folder,
                    }
                }
                const response = await fileAuthRequest(tempApplyFileData).unwrap()

                if (response.authUrl) {
                    connectWebSocket(response)
                }

                if (response.downloadUrl) {
                    // const validatedUrl = getValidatedImageUrl(response.downloadUrl)
                    // setImageUrl(validatedUrl || undefined)
                    setDownloadUrl(response.downloadUrl)

                    // Завантажуємо зображення для отримання розмірів
                    const img = new Image()
                    img.onload = () => {
                        const width = img.naturalWidth
                        const height = img.naturalHeight
                        setImageDimensions({ width, height })
                    }
                    img.src = response.downloadUrl || ''
                }
            } catch (e) {
                console.error('download error', e)
            }
        }

        loadImage()

        return () => {
            if (wsRef.current) {
                wsRef.current.close()
            }

            if (imageUrl) {
                URL.revokeObjectURL(imageUrl)
            }
        }
    }, [isOpen, previewFile, fileAuthRequest])

    const sendWebSocketMessage = useCallback((message: any) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message))
        } else {
            console.warn('WebSocket is not connected')
        }
    }, [])

    const requestImageUpdate = useCallback(() => {
        sendWebSocketMessage({
            type: 'request_image_update',
            fileId: previewFile?.CandidateFileID,
        })
    }, [previewFile?.CandidateFileID, sendWebSocketMessage])

    const handleZoomChange = (value: 'decrease' | 'increase') => {
        if (value === 'decrease') {
            setZoom((prev) => {
                if (prev > 200) {
                    setIsFitToScreen(false)
                    return 200
                }
                return Math.max(25, prev - 25)
            })
        } else {
            setZoom((prev) => Math.min(200, prev + 25))
        }
    }

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closePreview()
            }
            const isModifierPressed = e.ctrlKey || e.metaKey

            if (isModifierPressed) {
                if (e.key === '=' || e.key === '+') {
                    e.preventDefault()
                    handleZoomChange('increase')
                }

                if (e.key === '-' || e.key === '_') {
                    e.preventDefault()
                    handleZoomChange('decrease')
                }

                if (e.key === '0') {
                    e.preventDefault()
                    setZoom(100)
                }
            }
        },
        [closePreview]
    )

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = prevOverflow || 'unset'
        }
    }, [isOpen, handleKeyDown])

    const lastModified = (() => {
        if (previewFile && 'lastModified' in previewFile) {
            return previewFile.lastModified || 0
        } else if (previewFile && 'uploaded' in previewFile) {
            const uploaded = Date.parse(previewFile.uploaded ?? '')
            return isNaN(uploaded) ? 0 : uploaded
        } else if (previewFile && 'Uploaded' in previewFile) {
            const uploaded = Date.parse(previewFile.Uploaded ?? '')
            return isNaN(uploaded) ? 0 : uploaded
        }
        return 0
    })()

    const formattedDate = lastModified
        ? new Date(lastModified).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : ''

    const isLoadingSpinner = useMemo(() => {
        return isOpen && (isBinaryLoading || !imagesLoaded)
    }, [isOpen, isBinaryLoading, imagesLoaded])

    const handleDownload = () => {
        // const validatedUrl = getValidatedImageUrl(downloadUrl)
        // console.log(validatedUrl, imageUrl)
        const downUrl = downloadUrl
        if (downUrl) {
            const link = document.createElement('a')
            link.href = downUrl
            link.download = getFileName() || 'image'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const imageStyle = useMemo(() => {
        if (!imageDimensions) return {}

        const scaledWidth = imageDimensions.width * (zoom / 100)
        // const scaledHeight = imageDimensions.height * (zoom / 100)

        return {
            width: isFitToScreen ? `calc(100% - 36px)` : `${scaledWidth}px`,
            height: 'auto',
            maxWidth: '100%',
            objectFit: 'contain' as const,
        }
    }, [imageDimensions, zoom, isFitToScreen])

    return (
        <div className={`${baseClass} ${isOpen ? `${baseClass}--open` : ''}`}>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__left`}>
                    <div className={`${baseClass}__header__left__name`}>{getFileName()}</div>
                    <div className={`${baseClass}__header__left__date`}>{formattedDate}</div>
                </div>

                <div className={`${baseClass}__header__right`}>
                    <div className={`${baseClass}__zoom`}>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-decrease"
                            onClick={() => handleZoomChange('decrease')}
                            disabled={zoom <= 25}
                        >
                            <span>–</span>
                        </button>
                        <span>{zoom}%</span>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-increase"
                            onClick={() => handleZoomChange('increase')}
                            disabled={zoom >= 200}
                        >
                            <span>+</span>
                        </button>
                    </div>
                    <div className={`${baseClass}__header__right__nav`}>
                        {!isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(true)

                                    setZoom(
                                        Math.round(
                                            ((containerRef?.current?.getBoundingClientRect()?.width ?? 1) /
                                                (imageDimensions?.width ?? 1)) *
                                                100
                                        )
                                    )
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Expand-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M14.75.85a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 1 0 1.272 1.272l6.964-6.963V9.25a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9zm-5.5 22.3a.9.9 0 1 0 0-1.8H3.923l6.963-6.964a.9.9 0 1 0-1.272-1.272L2.65 20.077V14.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Expand-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        {isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(false)
                                    setZoom(100)
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Collapse-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M21.25 11.15a.9.9 0 1 0 0-1.8h-5.327l6.964-6.964a.9.9 0 1 0-1.273-1.272L14.65 8.077V2.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9zm-18.5 1.7a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 0 0 1.272 1.273l6.964-6.964v5.327a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Collapse-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        <button
                            className={`${baseClass}__button`}
                            aria-label="print"
                            onClick={() => {
                                handlePrint(imageBlob)
                            }}
                            // disabled={!imageBlob}
                        >
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M6.94 2.9a.1.1 0 0 0-.1.1v2.975h10.32V3a.1.1 0 0 0-.1-.1zm12.02 3.075V3a1.9 1.9 0 0 0-1.9-1.9H6.94A1.9 1.9 0 0 0 5.04 3v2.975H3a1.9 1.9 0 0 0-1.9 1.9v10.168c0 1.05.85 1.9 1.9 1.9h2.04V21c0 1.05.85 1.9 1.9 1.9h10.12a1.9 1.9 0 0 0 1.9-1.9v-1.057h1.828c1.062 0 2.112-.786 2.112-1.975V7.875a1.9 1.9 0 0 0-1.9-1.9zM3 7.775a.1.1 0 0 0-.1.1v10.168a.1.1 0 0 0 .1.1h2.04V16.28c0-1.05.85-1.9 1.9-1.9h10.12c1.05 0 1.9.85 1.9 1.9v1.863h1.828c.12 0 .209-.043.26-.088.05-.044.052-.076.052-.087V7.875a.1.1 0 0 0-.1-.1zm3.94 8.405a.1.1 0 0 0-.1.1V21a.1.1 0 0 0 .1.1h10.12a.1.1 0 0 0 .1-.1v-4.72a.1.1 0 0 0-.1-.1z"
                                        clipRule="evenodd"
                                    ></path>
                                </svg>
                            </div>
                            <span>{getTitle('1021', 'Print')}</span>
                        </button>
                        {downloadUrl && (
                            <button className={`${baseClass}__button`} aria-label="download" onClick={handleDownload}>
                                <div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="1em"
                                        height="1em"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.9 3.5a.9.9 0 1 0-1.8 0v10.827l-2.464-2.463a.9.9 0 1 0-1.272 1.272l4 4a.9.9 0 0 0 1.272 0l4-4a.9.9 0 1 0-1.272-1.272L12.9 14.327zM2 16.6a.9.9 0 0 1 .9.9V19c0 .335.268.6.595.6h17.008a.597.597 0 0 0 .597-.6v-1.5a.9.9 0 1 1 1.8 0V19c0 1.323-1.068 2.4-2.396 2.4H3.495A2.396 2.396 0 0 1 1.1 19v-1.5a.9.9 0 0 1 .9-.9"
                                            clipRule="evenodd"
                                        ></path>
                                    </svg>
                                </div>
                                <span>{getTitle('2342', 'Download')}</span>
                            </button>
                        )}
                    </div>
                </div>
                <button
                    className={`${baseClass}__button ${baseClass}__close-button`}
                    onClick={closePreview}
                    aria-label="Close preview"
                >
                    <span>✕</span>
                </button>
            </div>
            <div className={`${baseClass}__content`} ref={containerRef}>
                {isLoadingSpinner && <div className={`${baseClass}__content__spinner`} />}

                {imageUrl && (
                    <div className={`${baseClass}__image-container`}>
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt={getFileName() || 'Preview'}
                            style={imageStyle}
                            className={`${baseClass}__image`}
                            onLoad={() => setImagesLoaded(true)}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default FileImagePreview
